import { Link } from "wouter";
import { PageMeta, ORGANIZATION_JSONLD } from "@/components/PageMeta";
import { ExternalLink } from "@/components/ExternalLink";
import { SOPHIE_URL } from "@/lib/sophie-url";
import "./home-v2.css";

interface Capability {
  n: string;
  title: string;
  desc: string;
  href: string;
  cta: string;
  external?: boolean;
}

const CAPABILITIES: Capability[] = [
  {
    n: "01",
    title: "Sophie Platform",
    desc: "Managed contact and customer-service platform for regulated industries. Sophie handles calls, chat, SMS and email, captures structured data, triggers downstream actions, and gives your team full visibility through the Governance Centre. Governed, auditable, always on.",
    href: SOPHIE_URL,
    cta: "Explore Sophie",
    external: true,
  },
  {
    n: "02",
    title: "AI + Data Integration",
    desc: "Transform fragmented data into operational intelligence. We connect your systems, surface actionable insights, and support decisions your team still makes — revenue leakage dashboards, coding anomaly detection, care plan mismatch identification.",
    href: "/what-we-do/ai-data",
    cta: "AI + Data →",
  },
  {
    n: "03",
    title: "D365 Business Solutions",
    desc: "Microsoft Dynamics 365 for customer service, contact centres, and business operations. We configure, deploy, and support D365 solutions that fit your service model — not the other way around — with audit trails, role-based access, and structured workflows as standard.",
    href: "/what-we-do/d365",
    cta: "D365 Solutions →",
  },
  {
    n: "04",
    title: "Intelligent Process Automation",
    desc: "Free your teams from manual, repetitive work. Using UiPath, Power Platform, and Azure automation, we identify the highest-value automation opportunities and build solutions that run reliably — with governance your team can trust and full visibility into what's running and why.",
    href: "/what-we-do/automation",
    cta: "Process Automation →",
  },
  {
    n: "05",
    title: "Strategic Advisory + UpliftX",
    desc: "Not sure where to start? Our UpliftX framework maps how work actually flows across your operation, assesses where AI and automation genuinely fit, and builds a prioritised roadmap — one business unit at a time. We start with understanding, not with technology.",
    href: "/what-we-do/upliftx",
    cta: "UpliftX Advisory →",
  },
];

interface Industry {
  href: string;
  bar: string;
  name: string;
  desc: string;
  signalN: string;
  signalL: string;
}

const INDUSTRIES: Industry[] = [
  {
    href: "/industries/aged-care",
    bar: "var(--hv-ac)",
    name: "Aged Care",
    desc: "After-hours service automation, customer enquiries, co-contribution billing, shift management, compliance documentation, and clinical governance support under the Aged Care Act 2024.",
    signalN: "59%",
    signalL: "of aged care homes operated at a loss in 2024 — staff costs consuming 81.5% of operating revenue.",
  },
  {
    href: "/industries/ndis",
    bar: "var(--hv-nd)",
    name: "NDIS / Disability",
    desc: "Participant scheduling, intake workflows, service coordination, compliance documentation, and plan budget tracking — aligned to NDIS Quality and Safeguards Commission requirements.",
    signalN: "160k fewer",
    signalL: "NDIS participants projected over four years — tighter budgets, more scrutiny on every claim.",
  },
  {
    href: "/industries/healthcare",
    bar: "var(--hv-hc)",
    name: "Healthcare",
    desc: "Patient enquiry handling, appointment coordination, referral routing, and support for clinical documentation workflows.",
    signalN: "Privacy Act 1988",
    signalL: "Patient communication automation designed to operate within Australian Privacy Principles.",
  },
  {
    href: "/industries/financial-services",
    bar: "var(--hv-fs)",
    name: "Financial Services",
    desc: "Client enquiries, claims intake, compliance-sensitive communications, documentation workflows, and operational resilience automation — designed to operate within APRA CPS 230 and related frameworks.",
    signalN: "CPS 230",
    signalL: "Raising the bar for operational resilience and AI vendor governance across financial services.",
  },
  {
    href: "/industries/education",
    bar: "var(--hv-ed)",
    name: "Education",
    desc: "Staff scheduling, parent enquiries, site communication, compliance reporting, and enrolment management workflows — reducing administrative load on teaching and support staff.",
    signalN: "Administration",
    signalL: "Growing faster than staffing budgets across independent and government-funded schools.",
  },
  {
    href: "/industries/government",
    bar: "var(--hv-gv)",
    name: "Government",
    desc: "Citizen enquiries, service referrals, eligibility workflows, access coordination, and compliance reporting — with the governance controls government-adjacent services are required to maintain.",
    signalN: "Efficiency mandate",
    signalL: "Government-funded services expected to deliver more with tighter margins and stronger accountability.",
  },
];

const PHASES = [
  {
    n: "01",
    title: "Discover",
    desc: "We start by understanding your operation — the people, the processes, the systems, and the pain points. We listen before we recommend. Discovery produces a clear picture of where you are, what's working, and where the genuine opportunities are.",
  },
  {
    n: "02",
    title: "Design",
    desc: "Using insights from discovery, we design solutions that fit your environment — not the other way around. Every design considers people, process, and technology together. We don't automate broken processes; we fix them first.",
  },
  {
    n: "03",
    title: "Deploy",
    desc: "We build, configure, test, and go live. Change management, training, and support are included — because technology only works when people adopt it. Every deployment follows a governed process: tested in non-production, reviewed, and deployed with rollback capability.",
  },
  {
    n: "04",
    title: "Improve",
    desc: "We measure both return on investment and return on experience. Then we use live operational data to improve — governed, evidence-based, never autonomous. The Governance Centre and operational reporting provide the evidence base for every refinement we recommend.",
  },
];

export default function Home() {
  return (
    <div className="home-v2">
      <PageMeta
        title="Taidotech — Technology that works for the people who deliver care"
        description="Taidotech designs, builds and operates AI platforms and process automation for regulated Australian organisations — aged care, healthcare, NDIS and government."
        canonical="/"
        jsonLd={ORGANIZATION_JSONLD}
      />

      {/* Hero */}
      <section className="hv-hero">
        <div className="hv-wrap">
          <h1>
            AI and automation built for organisations where{" "}
            <em>governance isn't optional.</em>
          </h1>
          <p className="hv-hero-sub">
            We design, build, and operate AI platforms, business solutions, and process automation for Australian service organisations — aged care, healthcare, disability, financial services, education, and government. Any regulated environment where governance and accountability aren't optional.
          </p>
          <div className="hv-hero-ctas">
            <Link href="/what-we-do/" className="hv-btn hv-btn-coral-lg">
              What we do
            </Link>
            <Link href="/contact/" className="hv-btn hv-btn-outline-white">
              Start a conversation
            </Link>
          </div>
          <div className="hv-hero-proof">
            <div className="hv-hproof">
              <div className="hv-hproof-label">Australian-owned and operated</div>
              <p className="hv-hproof-text">
                Built specifically for Australian regulatory frameworks. Hosted in Azure East Australia. Voice and durable data are stored in Australia East. Full residency detail is published at taidotech.com.au/sophie/security.
              </p>
            </div>
            <div className="hv-hproof">
              <div className="hv-hproof-label">We build, deploy, and operate</div>
              <p className="hv-hproof-text">
                We're not a consulting firm that recommends and leaves. We build, deploy, and run the platforms we sell — so outcomes are our responsibility too.
              </p>
            </div>
            <div className="hv-hproof">
              <div className="hv-hproof-label">Governance by design</div>
              <p className="hv-hproof-text">
                Audit trails, controlled configuration, deterministic logic, and human accountability built in — not bolted on after the fact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Five capabilities */}
      <section className="hv-section" style={{ background: "#fff" }}>
        <div className="hv-wrap">
          <div className="hv-sec-head">
            <p className="hv-eyebrow">What we do</p>
            <h2>Five capabilities. One partner.</h2>
            <div className="hv-rule" />
            <p>
              We specialise in regulated industries — but the capabilities we build work for any organisation that values governance, structured outcomes, and practical technology. If accountability matters to your operation, we're a good fit.
            </p>
          </div>
          <div className="hv-cap-list">
            {CAPABILITIES.map((c) => (
              <div className="hv-cap-row" key={c.n}>
                <span className="hv-cap-n">{c.n}</span>
                <div className="hv-cap-title">{c.title}</div>
                <p className="hv-cap-desc">{c.desc}</p>
                {c.external ? (
                  <ExternalLink href={c.href} className="hv-cap-link">
                    {c.cta}
                  </ExternalLink>
                ) : (
                  <Link href={c.href} className="hv-cap-link">
                    {c.cta}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sophie band */}
      <section className="hv-sophie">
        <div className="hv-wrap">
          <div className="hv-sophie-grid">
            <div>
              <p className="hv-eyebrow">Sophie Platform</p>
              <h2>Every contact answered. Every interaction governed.</h2>
              <p className="hv-sophie-body">
                Sophie is a governed operational platform. Voice is one channel it works through. It handles calls, chat, SMS and email, applies your rules to decide what happens, records every interaction in full, and escalates to your people when it should.
              </p>
              <ul className="hv-soph-bullets">
                <li>Deterministic operational classification — rule-based, not unsupervised AI</li>
                <li>Full audit trail on every interaction</li>
                <li>Governed escalation, with real-time alerting to Microsoft Teams where configured</li>
                <li>Sophie does not self-modify in production</li>
                <li>Australian data sovereignty — hosted in Azure East Australia</li>
                <li>Managed by Taidotech — we operate it, you use it</li>
              </ul>
              <ExternalLink href={SOPHIE_URL} className="hv-btn hv-btn-coral-lg">
                Explore Sophie
              </ExternalLink>
            </div>
            <div className="hv-sophie-stats">
              <div className="hv-sstat">
                <div className="hv-sstat-n">24/7</div>
                <div className="hv-sstat-label">Always available</div>
                <div className="hv-sstat-sub">
                  No roster constraints. No capacity limits. Every contact handled through a governed response, around the clock.
                </div>
              </div>
              <div className="hv-sstat">
                <div className="hv-sstat-label">Structured response</div>
                <div className="hv-sstat-sub">
                  For voice, no caller reaches voicemail. Every interaction is logged, transcribed where applicable, and auditable.
                </div>
              </div>
              <div className="hv-sstat hv-sstat-prose">
                <div className="hv-sstat-label">Every contact answered</div>
                <div className="hv-sstat-sub">
                  Sophie takes on the calls, chat, SMS and email your team can't reach.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="hv-industries">
        <div className="hv-wrap">
          <div className="hv-sec-head">
            <p className="hv-eyebrow">Industries we help</p>
            <h2>We specialise in regulated industries. We're open to all.</h2>
            <div className="hv-rule" />
            <p>
              Our deepest experience is in regulated service environments — where governance, auditability, and accountability are operational requirements, not optional features. But the platforms we've built work for any organisation that takes governance seriously.
            </p>
          </div>
          <div className="hv-ind-grid">
            {INDUSTRIES.map((ind) => (
              <Link key={ind.href} href={ind.href} className="hv-ind-tile">
                <div className="hv-ind-bar" style={{ background: ind.bar }} />
                <div className="hv-ind-name">{ind.name}</div>
                <p className="hv-ind-desc">{ind.desc}</p>
                <div className="hv-ind-signal">
                  <span className="hv-ind-signal-n">{ind.signalN}</span>
                  <span className="hv-ind-signal-l">{ind.signalL}</span>
                </div>
                <span className="hv-ind-arrow" aria-hidden="true">↗</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Four phases */}
      <section className="hv-section hv-approach">
        <div className="hv-wrap">
          <div className="hv-sec-head">
            <p className="hv-eyebrow">Methodology</p>
            <h2>Four phases. One disciplined loop.</h2>
            <div className="hv-rule" />
            <p>
              Whether we're deploying Sophie, automating a workflow, or implementing D365, our approach puts understanding before technology and people before process.
            </p>
          </div>
          <div className="hv-phases">
            {PHASES.map((p) => (
              <div key={p.n} className="hv-phase">
                <span className="hv-phase-num">{p.n}</span>
                <div className="hv-phase-title">{p.title}</div>
                <p className="hv-phase-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="hv-cta-close">
        <div className="hv-wrap">
          <div className="hv-cta-grid">
            <div>
              <h2>Tell us about your operation.</h2>
              <p>
                Tell us what you're working on. We'll say plainly whether we can help. No obligation, no pitch deck.
              </p>
            </div>
            <div className="hv-cta-btns">
              <Link href="/contact/" className="hv-btn hv-btn-white">
                Start a conversation
              </Link>
              <a href="tel:0342326950" className="hv-btn hv-btn-ghost">
                Call 03 4232 6950
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
